import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Snackbar, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import axios from '../api/axios';

interface CatalogItem {
  catalogId: number;
  sku: string;
  name: string;
  price: number;
  quantity: number;
  pricePer: string;
  categoryName: string;
  special?: boolean;
  weight?: number;
}

interface OrderFormProps {
  userId?: number | string;
}

const OrderForm: React.FC<OrderFormProps> = ({ userId }) => {
  // ...existing code...
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [quantities, setQuantities] = useState<{ [key: number]: number | undefined }>({});
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [placedOrder, setPlacedOrder] = useState<any[]>([]);
  const navigate = useNavigate();
  const [orderError, setOrderError] = useState('');
  const [orderBatch, setOrderBatch] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    // Fetch current order batch first
    axios.get('/orderbatch/current')
      .then((response) => {
        setOrderBatch(response.data);
        const batchId = response.data?.batchId;
        if (!batchId) {
          setOrderError('No active order batch.');
          return;
        }
        // Fetch catalog items for this batch via order controller
        // include optional userId when provided
        const url = `/order?batchId=${batchId}` + (userId ? `&userId=${encodeURIComponent(String(userId))}` : '');
        axios.get(url)
          .then((catResponse) => {
            setCatalog(catResponse.data);
            // Set initial quantities from server's quantity value
            const initialQuantities: { [key: number]: number } = {};
            catResponse.data.forEach((item: CatalogItem) => {
              initialQuantities[item.catalogId] = item.quantity;
            });
            setQuantities(initialQuantities);
          })
          .catch(() => {
            setOrderError('Failed to load catalog for batch.');
          });
      })
      .catch(() => {
        setOrderError('Failed to load current order batch.');
      });
  }, [userId]);

  const handleQuantityChange = (id: number, value: string) => {
    // Allow empty string for easy clearing
    if (value === '') {
      setQuantities((q) => ({ ...q, [id]: undefined }));
      return;
    }
    let qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    setQuantities((q) => ({ ...q, [id]: qty }));
  };

  // Calculate total quantity and total dollar amount
  const totalQuantity = catalog.reduce((sum, item) => sum + (quantities[item.catalogId] || 0), 0);
  const subTotal = catalog.reduce((sum, item) => sum + ((quantities[item.catalogId] || 0) * (item.price || 0)), 0);
  const totalWeight = catalog.reduce((sum, item) => sum + ((quantities[item.catalogId] || 0) * (item.weight || 0)), 0);
  const taxRate = orderBatch?.taxRate || 0;
  const shippingRate = orderBatch?.shipping || 0;
  const tax = subTotal * taxRate;
  const shipping = totalWeight * shippingRate;
  const grandTotal = subTotal + tax + shipping;

  const handleOrderClick = () => {
    // Collect all items with quantity > 0
    const orderItems = catalog
  .filter(item => (quantities[item.catalogId] ?? 0) > 0)
      .map(item => ({
        catalogId: item.catalogId,
        quantity: quantities[item.catalogId],
        batchId: orderBatch.batchId
      }));
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (orderItems.length === 0 && !isAdmin) {
      setOrderError('Please enter quantity for at least one item.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmOrder = () => {
    const orderItems = catalog
  .filter(item => (quantities[item.catalogId] ?? 0) > 0)
      .map(item => ({
        catalogId: item.catalogId,
        quantity: quantities[item.catalogId],
        batchId: orderBatch.batchId
      }));
    // Build the URL with batchId and userId if provided
    let url = '/order';
    const params = [];
    if (orderBatch?.batchId !== undefined && orderBatch?.batchId !== null && orderBatch?.batchId !== '') {
      params.push(`batchId=${encodeURIComponent(String(orderBatch.batchId))}`);
    }
    if (userId !== undefined && userId !== null && userId !== '') {
      params.push(`userId=${encodeURIComponent(String(userId))}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    axios.post(url, orderItems)
      .then(() => {
        setOrderSuccess(true);
        setPlacedOrder(orderSummary);
        setShowConfirmation(true);
      })
      .catch(() => {
        setOrderError('Order failed.');
      });
    setConfirmOpen(false);
  };

  // Prepare order summary for modal
  const orderedItems = catalog.filter(item => (quantities[item.catalogId] ?? 0) > 0);
  const orderSummary = orderedItems.map(item => ({
    name: item.name,
    sku: item.sku,
    quantity: quantities[item.catalogId],
    price: item.price,
    pricePer: item.pricePer,
    categoryName: item.categoryName
  }));
  const deliveryDate = orderBatch?.deliveryDate ? new Date(orderBatch.deliveryDate).toLocaleDateString() : 'N/A';
  const orderDate = orderBatch?.orderDate ? new Date(orderBatch.orderDate).toLocaleDateString() : 'N/A';

  // Group catalog items by category
  const categories = Array.from(new Set(catalog.map(item => item.categoryName)));
  const catalogByCategory: { [key: string]: CatalogItem[] } = {};
  categories.forEach(cat => {
    catalogByCategory[cat] = catalog.filter(item => item.categoryName === cat);
  });

  return (
    <>
      <Container
        maxWidth={false}
        sx={{
          py: 2,
          px: { xs: 2, sm: 2 },
          width: { xs: '98%', md: '80%' },
          margin: { xs: '0 auto', md: '0 auto' },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'primary.main', fontSize: { xs: 15, md: 17 } }}>
            Please select the quantity of each item you would like to order and then press the order button at the bottom of the screen to review and confirm your order.
          </Typography>
        </Box>
        {categories.map(category => (
          <React.Fragment key={category}>
            <Typography variant="h6" sx={{ mt: 4, mb: 1, color: 'primary.main', fontWeight: 700 }}>
              {category}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 2,
                borderRadius: 2,
                bgcolor: 'primary.dark',
                mb: 2,
                width: '100%',
                overflowX: 'auto',
                minWidth: 0,
                '@media (max-width:600px)': {
                  borderRadius: 0,
                  mb: 1,
                },
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 380,
                  width: '100%',
                  '@media (max-width:600px)': {
                    minWidth: 0,
                    width: '100%',
                    tableLayout: 'fixed',
                  },
                  tableLayout: { xs: 'fixed', md: 'auto' },
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: 10, md: 16},
                        color: 'primary.contrastText',
                        width: { xs: '25%', md: '15%' },
                        minWidth: 60,
                        maxWidth: { xs: '25%', md: '15%' },
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >SKU</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: 10, md: 16},
                        color: 'primary.contrastText',
                        width: { xs: '32%', md: '45%' },
                        minWidth: 80,
                        maxWidth: { xs: '32%',md: '45%' },
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >Description</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: 10, md: 16},
                        color: 'primary.contrastText',
                        width: { xs: '20%', md: '28%' },
                        minWidth: 60,
                        maxWidth: { xs: '20%', md: '28%' },
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >Price</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: 10, md: 16},
                        color: 'primary.contrastText',
                        width: { xs: '23%', md: '20%' },
                        minWidth: 40,
                        maxWidth: { xs: '23%', md: '20%' },
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalogByCategory[category].map((item, idx) => (
                    <TableRow
                      key={item.catalogId}
                      hover
                      sx={{
                        transition: 'background 0.2s',
                        backgroundColor: item.special ? '#fff59d' : (idx % 2 === 0 ? '#fff' : '#f5f7fa'),
                        '&:hover': { backgroundColor: item.special ? '#ffe082' : '#e3eafc' },
                        height: 36
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: 10, md: 16},
                          color: 'primary.main',
                          wordBreak: 'break-word',
                          width: { xs: 'auto', md: '15%' },
                          minWidth: 60,
                          maxWidth: { md: '15%' },
                        }}
                      >{item.sku}</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: 10, md: 16},                          
                          color: 'primary.main',
                          wordBreak: 'break-word',
                          width: { xs: 'auto', md: '45%' },
                          minWidth: 120,
                          maxWidth: { md: '45%' },
                        }}
                      >{item.name}</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: 10, md: 16},                          
                          wordBreak: 'break-word',
                          width: { xs: 'auto', md: '28%' },
                          minWidth: 140,
                          maxWidth: { md: '28%' },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          {`$${item.price?.toFixed(2)} / ${item.pricePer}`}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <TextField
                          type="number"
                          size="small"
                          
                          inputProps={{ step: 1, min: 0, style: { width: 35, textAlign: 'center', padding: 0 } }}
                          value={quantities[item.catalogId] === undefined ? '' : quantities[item.catalogId]}
                          onChange={(e) => handleQuantityChange(item.catalogId, e.target.value)}
                          sx={{ fontSize: { xs: 10, md: 16},
                          bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, width: 40, m: 0 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </React.Fragment>
        ))}
        <Box display="flex" alignItems="flex-start" mt={2} mb={2}>
          <Box sx={{ px: 2, py: 1, borderRadius: 2, boxShadow: 1, minWidth: 220, textAlign: 'left', bgcolor: '#bdbdbd' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'black' }}>
              {`Total: ${totalQuantity} Item${totalQuantity === 1 ? '' : 's'}`}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'black' }}>
              SubTotal: ${subTotal.toFixed(2)}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'black' }}>
              Tax ({(taxRate * 100).toFixed(2)}%): ${tax.toFixed(2)}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'black' }}>
              Shipping ({totalWeight.toFixed(2)} lbs × ${shippingRate.toFixed(2)}): ${shipping.toFixed(2)}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'black', mt: 1 }}>
              Grand Total: ${grandTotal.toFixed(2)}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mt={4}>
          <Button variant="contained" size="large" onClick={handleOrderClick} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 18, boxShadow: 2 }}>
            Order
          </Button>
        </Box>
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: '#fff' }}>Confirm Order</DialogTitle>
          <DialogContent sx={{ bgcolor: '#fff' }}>
            <DialogContentText sx={{ mb: 2 }}>
              Please review your order below.<br /><br />
              By clicking <b>Confirm</b>, you are committing to ordering this food and picking it up on the order delivery date: <b>{deliveryDate}</b>.  You can return to edit your order any time until the order date: <b>{orderDate}</b>.<br /><br />
            </DialogContentText>
            {orderSummary.length === 0 ? (
              <Typography color="error">No items selected.</Typography>
            ) : (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderSummary.map((item, idx) => (
                    <TableRow key={item.sku + idx}>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{`$${item.price.toFixed(2)} / ${item.pricePer}`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                SubTotal: ${subTotal.toFixed(2)}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Tax ({(taxRate * 100).toFixed(2)}%): ${tax.toFixed(2)}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Shipping ({totalWeight.toFixed(2)} lbs × ${shippingRate.toFixed(2)}): ${shipping.toFixed(2)}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                Grand Total: ${grandTotal.toFixed(2)}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ bgcolor: '#fff' }}>
            <Button onClick={() => setConfirmOpen(false)} color="inherit">Cancel</Button>
            <Button onClick={handleConfirmOrder} color="primary" variant="contained">Confirm</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={showConfirmation}
          maxWidth="sm"
          fullWidth
          // No onClose, cannot be closed except by button
        >
          <DialogTitle sx={{ bgcolor: '#fff' }}>Order Received!</DialogTitle>
          <DialogContent sx={{ bgcolor: '#fff' }}>
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
              Thank you for your order. Below is a summary of your order:
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {placedOrder.map((item, idx) => (
                    <TableRow key={item.sku + idx}>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{`$${item.price.toFixed(2)} / ${item.pricePer}`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Order Total: ${placedOrder.reduce((sum, item) => sum + (item.quantity * item.price), 0).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Alert severity="info">
                You will receive a final confirmation by email with the final shipping amount a day or two before delivery. <b>Do not pay anything until final confirmations come out.</b><br />
                Checks can be made payable to <b>Audra Edmunds</b>.<br />
                Your order must be picked up at Audra's house (<b>8309 Powhickery Dr, Mechanicsville, VA 23116</b>) on the order delivery date: <b>{orderBatch?.deliveryDate ? new Date(orderBatch.deliveryDate).toLocaleDateString() : 'N/A'}</b>.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', bgcolor: '#fff' }}>
            <Button variant="contained" color="primary" size="large" onClick={() => navigate('/')}>Return to Home</Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={orderSuccess} autoHideDuration={3000} onClose={() => setOrderSuccess(false)}>
          <Alert onClose={() => setOrderSuccess(false)} severity="success" sx={{ width: '100%' }}>
            🎉 Order placed successfully!
          </Alert>
        </Snackbar>
        <Snackbar open={!!orderError} autoHideDuration={4000} onClose={() => setOrderError('')}>
          <Alert onClose={() => setOrderError('')} severity="error" sx={{ width: '100%' }}>
            {orderError}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default OrderForm;
